import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export default function TelempathyManager() {
  const [tools, setTools] = useState<any[]>([]);
  const [tarotCards, setTarotCards] = useState<any[]>([]);
  const [spreads, setSpreads] = useState<any[]>([]);
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState<any>(null);
  const [editingCard, setEditingCard] = useState<any>(null);
  const [editingSpread, setEditingSpread] = useState<any>(null);
  const [editingContent, setEditingContent] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [toolsData, cardsData, spreadsData, contentData] = await Promise.all([
      supabase.from('telempathy_tools').select('*').order('sort_order'),
      supabase.from('tarot_cards').select('*').order('sort_order'),
      supabase.from('tarot_spreads').select('*').order('sort_order'),
      supabase.from('telempathy_content').select('*').order('category')
    ]);

    setTools(toolsData.data || []);
    setTarotCards(cardsData.data || []);
    setSpreads(spreadsData.data || []);
    setContent(contentData.data || []);
    setLoading(false);
  };

  const saveTool = async (tool: any) => {
    const { error } = tool.id
      ? await supabase.from('telempathy_tools').update(tool).eq('id', tool.id)
      : await supabase.from('telempathy_tools').insert([tool]);

    if (error) {
      toast({ title: 'Error saving tool', variant: 'destructive' });
    } else {
      toast({ title: 'Tool saved successfully' });
      setEditingTool(null);
      loadData();
    }
  };

  const deleteTool = async (id: string) => {
    const { error } = await supabase.from('telempathy_tools').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error deleting tool', variant: 'destructive' });
    } else {
      toast({ title: 'Tool deleted' });
      loadData();
    }
  };

  const saveCard = async (card: any) => {
    const { error } = card.id
      ? await supabase.from('tarot_cards').update(card).eq('id', card.id)
      : await supabase.from('tarot_cards').insert([card]);

    if (error) {
      toast({ title: 'Error saving card', variant: 'destructive' });
    } else {
      toast({ title: 'Card saved successfully' });
      setEditingCard(null);
      loadData();
    }
  };

  const deleteCard = async (id: string) => {
    const { error } = await supabase.from('tarot_cards').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error deleting card', variant: 'destructive' });
    } else {
      toast({ title: 'Card deleted' });
      loadData();
    }
  };

  const saveSpread = async (spread: any) => {
    const { error } = spread.id
      ? await supabase.from('tarot_spreads').update(spread).eq('id', spread.id)
      : await supabase.from('tarot_spreads').insert([spread]);

    if (error) {
      toast({ title: 'Error saving spread', variant: 'destructive' });
    } else {
      toast({ title: 'Spread saved successfully' });
      setEditingSpread(null);
      loadData();
    }
  };

  const deleteSpread = async (id: string) => {
    const { error } = await supabase.from('tarot_spreads').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error deleting spread', variant: 'destructive' });
    } else {
      toast({ title: 'Spread deleted' });
      loadData();
    }
  };

  const saveContent = async (contentItem: any) => {
    const { error } = contentItem.id
      ? await supabase.from('telempathy_content').update(contentItem).eq('id', contentItem.id)
      : await supabase.from('telempathy_content').insert([contentItem]);

    if (error) {
      toast({ title: 'Error saving content', variant: 'destructive' });
    } else {
      toast({ title: 'Content saved successfully' });
      setEditingContent(null);
      loadData();
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <Tabs defaultValue="tools" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="tools">Tools</TabsTrigger>
        <TabsTrigger value="tarot">Tarot Cards</TabsTrigger>
        <TabsTrigger value="spreads">Spreads</TabsTrigger>
        <TabsTrigger value="content">Content</TabsTrigger>
      </TabsList>

      <TabsContent value="tools" className="space-y-4">
        <Button onClick={() => setEditingTool({ tool_id: '', title: '', description: '', icon_name: '', category: 'intuitive', sort_order: 0, is_published: true })}>
          <Plus className="h-4 w-4 mr-2" /> Add Tool
        </Button>

        {editingTool && (
          <Card>
            <CardHeader>
              <CardTitle>{editingTool.id ? 'Edit' : 'New'} Tool</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tool ID</Label>
                <Input value={editingTool.tool_id} onChange={(e) => setEditingTool({ ...editingTool, tool_id: e.target.value })} />
              </div>
              <div>
                <Label>Title</Label>
                <Input value={editingTool.title} onChange={(e) => setEditingTool({ ...editingTool, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editingTool.description} onChange={(e) => setEditingTool({ ...editingTool, description: e.target.value })} />
              </div>
              <div>
                <Label>Icon Name (Lucide)</Label>
                <Input value={editingTool.icon_name} onChange={(e) => setEditingTool({ ...editingTool, icon_name: e.target.value })} placeholder="Heart, Brain, Eye, etc." />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={editingTool.category} onValueChange={(value) => setEditingTool({ ...editingTool, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="intuitive">Intuitive</SelectItem>
                    <SelectItem value="mediumship">Mediumship</SelectItem>
                    <SelectItem value="energy">Energy</SelectItem>
                    <SelectItem value="development">Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input type="number" value={editingTool.sort_order} onChange={(e) => setEditingTool({ ...editingTool, sort_order: parseInt(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingTool.is_published} onCheckedChange={(checked) => setEditingTool({ ...editingTool, is_published: checked })} />
                <Label>Published</Label>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveTool(editingTool)}><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button variant="outline" onClick={() => setEditingTool(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {tools.map((tool) => (
            <Card key={tool.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{tool.title}</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingTool(tool)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteTool(tool.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription>{tool.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="tarot" className="space-y-4">
        <Button onClick={() => setEditingCard({ card_name: '', card_number: 0, suit: '', keywords: [], story: '', upright_meaning: '', reversed_meaning: '', sort_order: 0, is_published: true })}>
          <Plus className="h-4 w-4 mr-2" /> Add Card
        </Button>

        {editingCard && (
          <Card>
            <CardHeader>
              <CardTitle>{editingCard.id ? 'Edit' : 'New'} Tarot Card</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Card Name</Label>
                <Input value={editingCard.card_name} onChange={(e) => setEditingCard({ ...editingCard, card_name: e.target.value })} />
              </div>
              {editingCard.image_url && (
                <div>
                  <Label>Current Image</Label>
                  <img src={editingCard.image_url} alt={editingCard.card_name} className="w-48 h-auto rounded-lg border" />
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e: any) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      toast({ title: 'Uploading image...' });
                      const fileName = `${editingCard.card_name?.toLowerCase().replace(/\s+/g, '-') || 'card'}-${Date.now()}.${file.name.split('.').pop()}`;
                      const { error } = await supabase.storage
                        .from('tarot-images')
                        .upload(fileName, file);
                      
                      if (error) {
                        toast({ title: 'Upload failed', variant: 'destructive' });
                      } else {
                        const { data: { publicUrl } } = supabase.storage
                          .from('tarot-images')
                          .getPublicUrl(fileName);
                        setEditingCard({ ...editingCard, image_url: publicUrl });
                        toast({ title: 'Image uploaded!' });
                      }
                    };
                    input.click();
                  }}
                >
                  Upload Image
                </Button>
              </div>
              <div>
                <Label>Card Number</Label>
                <Input type="number" value={editingCard.card_number} onChange={(e) => setEditingCard({ ...editingCard, card_number: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Suit</Label>
                <Input value={editingCard.suit} onChange={(e) => setEditingCard({ ...editingCard, suit: e.target.value })} />
              </div>
              <div>
                <Label>Keywords (comma-separated)</Label>
                <Input value={Array.isArray(editingCard.keywords) ? editingCard.keywords.join(', ') : ''} onChange={(e) => setEditingCard({ ...editingCard, keywords: e.target.value.split(',').map(k => k.trim()) })} />
              </div>
              <div>
                <Label>Story</Label>
                <Textarea value={editingCard.story} onChange={(e) => setEditingCard({ ...editingCard, story: e.target.value })} rows={4} />
              </div>
              <div>
                <Label>Upright Meaning</Label>
                <Textarea value={editingCard.upright_meaning} onChange={(e) => setEditingCard({ ...editingCard, upright_meaning: e.target.value })} rows={3} />
              </div>
              <div>
                <Label>Reversed Meaning</Label>
                <Textarea value={editingCard.reversed_meaning} onChange={(e) => setEditingCard({ ...editingCard, reversed_meaning: e.target.value })} rows={3} />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveCard(editingCard)}><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button variant="outline" onClick={() => setEditingCard(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {tarotCards.map((card) => (
            <Card key={card.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{card.card_name}</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingCard(card)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteCard(card.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="spreads" className="space-y-4">
        <Button onClick={() => setEditingSpread({ spread_name: '', description: '', positions: [], sort_order: 0, is_published: true })}>
          <Plus className="h-4 w-4 mr-2" /> Add Spread
        </Button>

        {editingSpread && (
          <Card>
            <CardHeader>
              <CardTitle>{editingSpread.id ? 'Edit' : 'New'} Spread</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Spread Name</Label>
                <Input value={editingSpread.spread_name} onChange={(e) => setEditingSpread({ ...editingSpread, spread_name: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editingSpread.description} onChange={(e) => setEditingSpread({ ...editingSpread, description: e.target.value })} />
              </div>
              <div>
                <Label>Positions (one per line)</Label>
                <Textarea 
                  value={Array.isArray(editingSpread.positions) ? editingSpread.positions.join('\n') : ''} 
                  onChange={(e) => setEditingSpread({ ...editingSpread, positions: e.target.value.split('\n').filter(p => p.trim()) })} 
                  rows={5} 
                  placeholder="Past / Situation&#10;Present / Challenge&#10;Future / Outcome"
                />
              </div>
              {editingSpread.image_url && (
                <div>
                  <Label>Current Image</Label>
                  <img src={editingSpread.image_url} alt={editingSpread.spread_name} className="w-full max-w-md rounded-lg border" />
                </div>
              )}
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e: any) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      toast({ title: 'Uploading image...' });
                      const fileName = `spread-${editingSpread.spread_name?.toLowerCase().replace(/\s+/g, '-') || 'spread'}-${Date.now()}.${file.name.split('.').pop()}`;
                      const { error } = await supabase.storage
                        .from('tarot-images')
                        .upload(fileName, file);
                      
                      if (error) {
                        toast({ title: 'Upload failed', variant: 'destructive' });
                      } else {
                        const { data: { publicUrl } } = supabase.storage
                          .from('tarot-images')
                          .getPublicUrl(fileName);
                        setEditingSpread({ ...editingSpread, image_url: publicUrl });
                        toast({ title: 'Image uploaded!' });
                      }
                    };
                    input.click();
                  }}
                >
                  Upload Spread Image
                </Button>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveSpread(editingSpread)}><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button variant="outline" onClick={() => setEditingSpread(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {spreads.map((spread) => (
            <Card key={spread.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{spread.spread_name}</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingSpread(spread)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteSpread(spread.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription>{spread.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="content" className="space-y-4">
        <Button onClick={() => setEditingContent({ content_key: '', content_text: '', category: 'general', description: '' })}>
          <Plus className="h-4 w-4 mr-2" /> Add Content
        </Button>

        {editingContent && (
          <Card>
            <CardHeader>
              <CardTitle>{editingContent.id ? 'Edit' : 'New'} Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Content Key</Label>
                <Input value={editingContent.content_key} onChange={(e) => setEditingContent({ ...editingContent, content_key: e.target.value })} placeholder="sitting_in_power_instructions" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={editingContent.category} onChange={(e) => setEditingContent({ ...editingContent, category: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editingContent.description} onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })} />
              </div>
              <div>
                <Label>Content Text</Label>
                <Textarea value={editingContent.content_text} onChange={(e) => setEditingContent({ ...editingContent, content_text: e.target.value })} rows={6} />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveContent(editingContent)}><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button variant="outline" onClick={() => setEditingContent(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {content.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{item.content_key}</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingContent(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription>{item.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
}